// client/src/components/Insights/AIInsightsPage.js
// Keeper AI — Premium Financial Intelligence Hub (thin orchestrator)

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Card, Tabs, Tab, Avatar, useTheme, useMediaQuery } from '@mui/material';
import { Brain } from 'lucide-react';
import ChatTab from './tabs/ChatTab';
import OverviewTab from './tabs/OverviewTab';
import SpendingTab from './tabs/SpendingTab';
import FireTab from './tabs/FireTab';
import SeasonalTab from './tabs/SeasonalTab';
import { GRADIENT, alpha } from './shared/constants';
import { useGlass } from './shared/SharedComponents';
import InsightsDateFilter from './shared/InsightsDateFilter';
import LockedFeature from '../Subscription/LockedFeature';

// Goals, cash flow, merchants and what-ifs live on their own pages (Goals, Cash Flow,
// Merchants, Paycheck plan); Overview links to them. Only tabs that read a period
// show the date filter: FIRE and Seasonal pick the window they need themselves.
const TAB_CONFIG = [
  { labelKey: 'aiInsights.tabs.askAI',    label: '💬 Ask AI',    component: ChatTab },
  { labelKey: 'aiInsights.tabs.overview', label: '📊 Overview',  component: OverviewTab, usesFilter: true },
  { labelKey: 'aiInsights.tabs.spending', label: '🧭 Spending patterns', component: SpendingTab, usesFilter: true },
  { labelKey: 'aiInsights.tabs.fire',     label: '🔥 FIRE',      component: FireTab },
  { labelKey: 'aiInsights.tabs.seasonal', label: '🌊 Seasonal',  component: SeasonalTab },
];

const AIInsightsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(1);
  const glass = useGlass();

  const ActiveComponent = TAB_CONFIG[activeTab].component;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{
        background: GRADIENT, borderRadius: 4, p: { xs: 3, md: 4 }, mb: 3, color: '#fff',
        display: 'flex', alignItems: 'center', gap: 2, position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, right: 60, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <Avatar sx={{ background: 'rgba(255,255,255,0.2)', width: 56, height: 56, backdropFilter: 'blur(8px)', boxShadow: '0 0 20px rgba(255,255,255,0.15)' }}>
          <Brain size={28} color="#fff" />
        </Avatar>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={900} sx={{ letterSpacing: -0.5 }}>
            {t('aiInsights.title', 'Keeper AI ✨')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {t('aiInsights.subtitleV2', 'Your health score, spending patterns, FIRE and seasonal trends — from your own data')}
          </Typography>
        </Box>
      </Box>

      {TAB_CONFIG[activeTab].usesFilter && <InsightsDateFilter />}

      {/* Tabs */}
      <Card sx={{ ...glass, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { minWidth: 'auto', px: { xs: 1.5, md: 2.5 }, fontSize: { xs: 11, md: 13 }, textTransform: 'none', fontWeight: 700, transition: 'all 0.2s ease' },
            '& .Mui-selected': { color: '#8b5cf6 !important' },
            '& .MuiTabs-indicator': { background: GRADIENT, height: 3, borderRadius: 2 },
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          }}
        >
          {TAB_CONFIG.map((tab, i) => (
            <Tab key={i} label={t(tab.labelKey, tab.label)} />
          ))}
        </Tabs>
      </Card>

      {/* Tab content (LockedFeature renders transparently for premium users;
          for free users it overlays the preview with an upgrade CTA) */}
      <LockedFeature
        feature="aiInsights"
        ctaPlan="premium"
        preview="overlay"
        promoText="AI-powered financial insights"
      >
        <Box key={activeTab} sx={{
          animation: 'tabFadeIn 0.25s ease',
          '@keyframes tabFadeIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
        }}>
          <ActiveComponent />
        </Box>
      </LockedFeature>
    </Container>
  );
};

export default AIInsightsPage;
